'use client';

import * as Yup from 'yup';
import {Formik} from 'formik';
import {useState} from 'react';
import styled from 'styled-components';
import {useTranslations} from 'next-intl';
import {
  LandingBigButton,
  MainTitle,
  DemoDescription,
  DemoAvatarsContainer,
  DemoAvatar
} from '../landingStyledComponents';
import {ColumnSection, ThankYouLayoutContainer} from 'src/components/pagesStyles';
import {panelistaPrimaryColor, panelistaLightColor} from 'src/utils/constants';
import InteractiveAvatar from '../landing/InteractiveAvatar';
import FormTextInput from 'src/components/FormTextInput';
import {clientRequest} from 'src/client/apiClient';
import getBrandStyle from 'src/utils/getBrandStyle';
import ClapImage from '../../icons/clapImage.svg';
import RemixIcon from 'src/components/RemixIcon';
import PhoneInput from '../PhoneInput';

const compactThreshold = 900;
const mobileThreshold = 600;

const getBookDemoFormSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    name: Yup.string().max(50, t('common.too long')).required(t('common.required')),
    phone: Yup.string().required(t('common.required')),
    email: Yup.string().max(100, t('common.too long')).required(t('common.required'))
  });

const brandStyle = getBrandStyle({primaryColor: panelistaPrimaryColor});

const CenterContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
`;

const HeroTitle = styled(MainTitle)`
  text-align: center;
  max-width: 900px;

  @media (max-width: ${mobileThreshold}px) {
    text-align: left;
  }
`;

const ContentBoxContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: 80px 0 120px 0;
  justify-content: space-around;

  @media (max-width: 1000px) {
    flex-direction: column-reverse;
    align-items: center;
    margin: 64px 0 64px 0;
  }

  @media (max-width: 820px) {
    margin: 64px 0 0px 0;
  }
`;

const ContentBox = styled.div`
  margin: 0 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 420px;

  > ul {
    @media (max-width: 1000px) {
      margin: 0 0 40px 0;
    }
  }
`;

const FormBox = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 24px;
  width: 100%;
  max-height: 100%;
  box-shadow: 0px 7px 30px rgba(0, 0, 0, 0.07);

  @media (max-width: ${compactThreshold}px) {
    margin-bottom: 20px;
  }
`;

const ListItem = styled.li`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 36px;
  display: flex;

  @media (max-width: 450px) {
    font-size: 16px;
  }
`;

const CheckCircle = styled.div`
  margin-right: 14px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${panelistaLightColor};
  border-radius: 20px;
  flex-shrink: 0;
`;

type FormType = {
  name?: string;
  phone?: string;
  email?: string;
};

const DemoHeroSection = () => {
  const t = useTranslations();
  const [isSent, setIsSent] = useState(false);
  const formSchema = getBookDemoFormSchema(t);

  const onSubmit = (formValues: FormType) => {
    const body = {...formValues, type: 'book-demo'};

    return clientRequest({url: '/forms/contact', options: {method: 'POST'}, body}).then(() => {
      plausible('Booked demo');
      setIsSent(true);
    });
  };

  return (
    <>
      <CenterContentSection>
        <HeroTitle>{t('website.demo page hero title')}</HeroTitle>
        <DemoDescription>{t('website.demo page subheading')}</DemoDescription>
      </CenterContentSection>

      <DemoAvatarsContainer>
        <DemoAvatar>
          <InteractiveAvatar
            alt="Joakim"
            pictureSrc="/images/joakimAvatar.jpg"
            altPictureSrc="/images/joakimAltAvatar.jpg"
            size={70}
            inView
          />
        </DemoAvatar>
        <DemoAvatar>
          <InteractiveAvatar
            alt="Charlotta"
            pictureSrc="/images/charlottaAvatar.jpg"
            altPictureSrc="/images/charlottaAltAvatar.jpg"
            size={70}
            inView
          />
        </DemoAvatar>
      </DemoAvatarsContainer>

      <ContentBoxContainer>
        <ContentBox>
          <ColumnSection>
            {isSent ? (
              <ThankYouLayoutContainer>
                <ClapImage viewBox="0 0 80 80" style={{width: 120, height: 120}} />
                <h2>{t('website.book demo thank you header')}</h2>
                <p>{t('website.book demo thank you body')}</p>
              </ThankYouLayoutContainer>
            ) : (
              <FormBox>
                <Formik
                  validationSchema={formSchema}
                  initialValues={{name: '', phone: '', email: ''}}
                  onSubmit={onSubmit}
                >
                  {(formikProps) => {
                    const {submitForm} = formikProps;

                    return (
                      <form>
                        <FormTextInput
                          formikProps={formikProps}
                          brandStyle={brandStyle}
                          id="name"
                          label={t('common.name')}
                        />

                        <FormTextInput
                          formikProps={formikProps}
                          brandStyle={brandStyle}
                          type="email"
                          id="email"
                          label={t('common.email')}
                        />

                        <PhoneInput formikProps={formikProps} brandStyle={brandStyle} id="phone" />

                        <LandingBigButton
                          as="button"
                          id="submit-demo-request"
                          type="submit"
                          onClick={submitForm}
                          style={{width: '100%'}}
                        >
                          {t('website.book demo')}
                        </LandingBigButton>
                      </form>
                    );
                  }}
                </Formik>
              </FormBox>
            )}
          </ColumnSection>
        </ContentBox>
        <ContentBox style={{maxWidth: 420}}>
          <ul>
            <ListItem>
              <CheckCircle>
                <RemixIcon name="check-line" color={panelistaPrimaryColor} size={24} />
              </CheckCircle>
              {t('website.book demo point 1')}
            </ListItem>
            <ListItem>
              <CheckCircle>
                <RemixIcon name="check-line" color={panelistaPrimaryColor} size={24} />
              </CheckCircle>
              {t('website.book demo point 2')}
            </ListItem>
            <ListItem>
              <CheckCircle>
                <RemixIcon name="check-line" color={panelistaPrimaryColor} size={24} />
              </CheckCircle>
              {t('website.book demo point 3')}
            </ListItem>
          </ul>
        </ContentBox>
      </ContentBoxContainer>
    </>
  );
};

export default DemoHeroSection;
